import React, { useRef } from 'react';
import { MImage as Image } from '@shared/h5/components';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { useHover, useLockFn } from 'ahooks';
import { isEmpty } from 'lodash';
import pingEvent from '@shared/h5/utils/voyager/pingEvent';
import { useIsMobile } from '@shared/h5/hooks/use-mobile-detect';
import delay from '@/common/utils/delay';
import { OpenAppFromTop } from '@shared/h5/components/OpenApp';

export interface User {
  avatar?: string;
  name?: string;
  position?: string;
  company?: string;
}

export interface HeaderProps {
  user: User;
}

const Header: React.FC<HeaderProps> = ({ user }) => {
  const refAvatar = useRef<HTMLDivElement>(undefined);

  const isHovering = useHover(refAvatar);

  const router = useRouter();

  const isMobile = useIsMobile();

  const login = useLockFn(async () => {
    pingEvent('top_login_or_reg_click', {});
    await delay(200);

    const { href } = window.location;
    window.location.href = `https://maimai.cn/platform/login?to=${encodeURIComponent(href)}`;
  });

  const logout = () => {
    const { href } = window.location;
    window.location.href = `https://maimai.cn/sdk/platform/logout?to=${encodeURIComponent(href)}`;
  };

  const rightRender = () => {
    if (isEmpty(user)) {
      return (
        <div
          className='cursor-pointer whitespace-nowrap rounded-2xl bg-[#0052ff] px-4 text-sm leading-8 text-white'
          onClick={login}
        >
          登录 / 注册
        </div>
      );
    }
    return (
      <div ref={refAvatar} className='flex'>
        <Image
          className='cursor-pointer overflow-hidden rounded-full'
          width='32'
          height='32'
          alt='avatar'
          src={user.avatar}
        />
        <div
          className={classNames(
            'absolute top-[50px] right-3 w-[182px] rounded-2xl bg-white py-1 px-2 shadow-xl md:right-12',
            {
              hidden: !isHovering,
            }
          )}
        >
          <div className='flex items-center p-2'>
            <div className='relative mr-2 h-7 w-7 flex-none rounded-full'>
              <Image className='rounded-full' width='28' height='28' alt='avatar' src={user.avatar} />
            </div>
            <div className='w-28'>
              <div className='text-sm font-bold text-[#15161f]'>{user.name}</div>
              <h2 className='truncate text-xs text-[#afb1bc]'>{(user.company || '') + (user.position || '')}</h2>
            </div>
          </div>
          <div className='cursor-pointer p-2 text-sm hover:rounded-lg hover:bg-[#f4f5fa]' onClick={logout}>
            退出登录
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='relative h-16'>
      <div className='fixed left-2/4 right-0 top-0 bottom-0 z-10 flex h-16 w-full max-w-7xl -translate-x-1/2 items-center justify-between bg-[#f4f5fa] bg-opacity-90 px-3 backdrop-blur-[6px] backdrop-saturate-[180%] md:px-12'>
        {isMobile && router.query.share_channel ? (
          <OpenAppFromTop
            contentType='www_growth_cb_article'
            clipboardKey={`www_growth_cb_article***${router.query.fid}***${router.query.share_uid}`}
          />
        ) : (
          <>
            <a href='/' title='脉脉'>
              <Image width='61' height='28' alt='logo' src='https://i9.taou.com/maimai/p/30462/1802_6_7fwucZQaXmNBTK' />
            </a>
            {rightRender()}
          </>
        )}
      </div>
    </div>
  );
};

export default Header;
